import { useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useExpertTipFilters } from '@/hooks/useExpertTipFilters';
import { useExpertTips } from '@/hooks/useExpertTips';
import { useActiveExpertProfiles } from '@/hooks/useExpertProfiles';
import { useExpertTipFilterOptions } from '@/hooks/useExpertTipFilterOptions';

import ExpertTipsTable from '@/components/ExpertTipsTable';
import { Pagination } from '@/components/Pagination';
import type { ExpertTipFilters, ExpertTipSkill } from '@/types/expert-tip';

const selectClass =
  'px-4 py-2 rounded-pine-input border border-pine-line bg-white text-pine-ink focus:border-pine-pine focus:outline-none transition-colors';

/** Groups skills by domain, keeping API order (already sorted by domain) */
function groupSkillsByDomain(skills: ExpertTipSkill[]) {
  const groups = new Map<number, { domainTitle: string; skills: ExpertTipSkill[] }>();
  for (const skill of skills) {
    const group = groups.get(skill.domainId) ?? { domainTitle: skill.domainTitle, skills: [] };
    group.skills.push(skill);
    groups.set(skill.domainId, group);
  }
  return [...groups.entries()].map(([domainId, group]) => ({ domainId, ...group }));
}

export default function ExpertTipsAdminPage() {
  const navigate = useNavigate();
  const { filters, updateFilter, updateFilters, clearFilters, hasActiveFilters } = useExpertTipFilters();
  const { data, isLoading, isError, error } = useExpertTips(filters);
  const { data: activeExperts } = useActiveExpertProfiles();
  const { data: filterOptions } = useExpertTipFilterOptions();

  const selectedDomainId =
    filters.domainId && filters.domainId !== 'none' ? Number(filters.domainId) : null;

  const skillGroups = useMemo(() => {
    const skills = filterOptions?.skills ?? [];
    return groupSkillsByDomain(
      selectedDomainId === null ? skills : skills.filter((s) => s.domainId === selectedDomainId)
    );
  }, [filterOptions?.skills, selectedDomainId]);

  /** Clears a skill filter from another domain; keeps it for the same domain, "All" or "No domain" */
  const handleDomainChange = (value: string) => {
    const newDomainId = value && value !== 'none' ? Number(value) : null;
    const selectedSkill = filterOptions?.skills.find((s) => String(s.id) === filters.domainSkillId);
    const keepSkill = newDomainId === null || selectedSkill?.domainId === newDomainId;
    updateFilters({ domainId: value, domainSkillId: keepSkill ? filters.domainSkillId : '' });
  };

  useEffect(() => {
    document.title = 'Expert Tips | Admin Dashboard';
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, []);

  useEffect(() => {
    if (isError && error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load expert tips');
    }
  }, [isError, error]);

  const handleSort = useCallback(
    (column: ExpertTipFilters['sortBy']) => {
      if (filters.sortBy === column) {
        updateFilter('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        updateFilters({ sortBy: column, sortOrder: 'asc' });
      }
    },
    [filters.sortBy, filters.sortOrder, updateFilter, updateFilters]
  );

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-pine-display font-display font-semibold text-pine-ink">Expert Tips</h1>
            <p className="text-pine-body text-pine-ink-soft mt-1">
              Manage expert tips for the Expert Tips feature
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/expert-tips/new')}
            className="admin-button-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            <span>New Expert Tip</span>
          </button>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex flex-wrap items-center gap-4">
          <select
            value={filters.expertId}
            onChange={(e) => updateFilter('expertId', e.target.value)}
            className="px-4 py-2 rounded-pine-input border border-pine-line bg-white text-pine-ink focus:border-pine-pine focus:outline-none transition-colors"
          >
            <option value="">All Experts</option>
            {activeExperts?.map((expert) => (
              <option key={expert.id} value={expert.id}>
                {expert.name}
              </option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value as ExpertTipFilters['status'])}
            className="px-4 py-2 rounded-pine-input border border-pine-line bg-white text-pine-ink focus:border-pine-pine focus:outline-none transition-colors"
          >
            <option value="">All Statuses</option>
            <option value="published">Published</option>
            <option value="unpublished">Unpublished</option>
            <option value="archived">Archived</option>
          </select>

          <select
            value={filters.domainId}
            onChange={(e) => handleDomainChange(e.target.value)}
            className={selectClass}
          >
            <option value="">All Domains</option>
            <option value="none">No domain</option>
            {filterOptions?.domains.map((domain) => (
              <option key={domain.id} value={domain.id}>
                {domain.title}
              </option>
            ))}
          </select>

          <select
            value={filters.domainSkillId}
            onChange={(e) => updateFilter('domainSkillId', e.target.value)}
            className={selectClass}
          >
            <option value="">All Skills</option>
            {skillGroups.map((group) => (
              <optgroup key={group.domainId} label={group.domainTitle}>
                {group.skills.map((skill) => (
                  <option key={skill.id} value={skill.id}>
                    {skill.title}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-pine-ink-soft hover:text-pine-ink underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </motion.div>

      {/* Error State */}
      {isError && (
        <div className="admin-card p-6 border border-red-500/20 bg-red-500/5">
          <p className="text-red-400">Failed to load expert tips. Please try again.</p>
        </div>
      )}

      {/* Table */}
      {!isError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <ExpertTipsTable
            tips={data?.items || []}
            isLoading={isLoading}
            isEmpty={!isLoading && data?.total === 0 && !hasActiveFilters}
            sortBy={filters.sortBy}
            sortOrder={filters.sortOrder}
            onSort={handleSort}
          />
        </motion.div>
      )}

      {/* Pagination */}
      {!isError && data && totalPages > 1 && (
        <Pagination
          currentPage={filters.page}
          totalPages={totalPages}
          totalItems={data.total}
          pageSize={filters.pageSize}
          onPageChange={(page) => updateFilter('page', page)}
        />
      )}
    </div>
  );
}
